import { Alert, Box, Link, Typography } from "@mui/material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxSelectSingle from "components/UI/Select/BaseSelectComponents/AcxSelectSingle";
import { observer } from "mobx-react";
import {
    ConfigurationBody,
    EmissionsConfiguration,
    EndPointReference,
} from "models/Emissions";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useSearchParams } from "react-router-dom";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import { useStore } from "utils/useStore";
import OrganizationBreadcrumbs from "../OrganizationBreadcrumbs";
import {
    ConfigurationStatusChip,
    ConfigurationTypeChip,
} from "./EmissionsChips";
import EmissionsFormErrorBanner from "./EmissionsFormErrorBanner";
import EmissionsJsonField, {
    EmissionsJsonFieldRef,
    parseJsonText,
    toJsonText,
} from "./EmissionsJsonField";
import ConfigurationStatusDialog from "./ConfigurationStatusDialog";
import CreateBodyConfirmationDialog from "./CreateBodyConfirmationDialog";
import { getSaveErrorMessages } from "./emissionsErrors";
import {
    getBodyEndpointId,
    useEmissionsConfigurations,
} from "./useEmissionsConfigurations";

type FormState = {
    name: string;
    description: string;
    endpointId: string;
    ruleMap: string;
    requestBody: string;
};

type EndpointOption = { id: string; label: string };

const newRequestBodyTemplate = toJsonText({
    MessageFormat: { structure: {}, mappings: {} },
});

const toRequestBodyText = (configuration: ConfigurationBody | null) => {
    if (!configuration) return "";
    const {
        RulesFilter: _rulesFilter,
        endPoints: _endPoints,
        ...rest
    } = configuration;
    return toJsonText(rest);
};

const toFormState = (body: EmissionsConfiguration): FormState => ({
    name: body.name ?? "",
    description: body.description ?? "",
    endpointId: getBodyEndpointId(body) ?? "",
    ruleMap: toJsonText(body.configuration?.RulesFilter),
    requestBody: toRequestBodyText(body.configuration),
});

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
    !!value && typeof value === "object" && !Array.isArray(value);

type WarningState = { emissionsWarnings?: string[] } | null;

const toWarningMessages = (
    warnings: { path?: string | null; message: string }[] | null | undefined,
) =>
    (warnings ?? []).map((warning) =>
        warning.path ? `${warning.path}: ${warning.message}` : warning.message,
    );

const EmissionBodyForm: React.FC = observer(() => {
    const { bodyId } = useParams<{ bodyId?: string }>();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const location = useLocation();
    const authStore = useStore(AuthStore);
    const messageStore = useStore(MessageStore);
    const canEdit = authStore.canUserEdit("Emissions Configuration");
    const { service, endpoints, bodies, isLoading, hasError, reload } =
        useEmissionsConfigurations();

    const isNew = !bodyId;
    const [form, setForm] = useState<FormState>(() => ({
        name: "",
        description: "",
        endpointId: searchParams.get("endpointId") ?? "",
        ruleMap: "",
        requestBody: newRequestBodyTemplate,
    }));
    const [saved, setSaved] = useState<EmissionsConfiguration>();
    const [saveErrors, setSaveErrors] = useState<string[]>([]);
    const [saveWarnings, setSaveWarnings] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);
    const [statusDialog, setStatusDialog] = useState<"disable" | "enable">();
    const [isConfirmingCreate, setIsConfirmingCreate] = useState(false);
    const [isChangingStatus, setIsChangingStatus] = useState(false);
    const [jsonValidity, setJsonValidity] = useState({
        ruleMap: true,
        requestBody: true,
    });
    const ruleMapRef = useRef<EmissionsJsonFieldRef>(null);
    const requestBodyRef = useRef<EmissionsJsonFieldRef>(null);
    const loadedIdRef = useRef<string>();

    const loadedBody = useMemo(
        () =>
            bodies.find(
                (body) => body.id.toLowerCase() === bodyId?.toLowerCase(),
            ),
        [bodies, bodyId],
    );
    const body = saved ?? loadedBody;

    // Warnings raised while creating arrive as route state on the redirect to the saved record.
    // Both routes render this same component, so React may keep it mounted and skip a remount;
    // reading the state in an effect covers that case as well as a fresh mount.
    const routeWarnings = (location.state as WarningState)?.emissionsWarnings;
    useEffect(() => {
        if (routeWarnings) setSaveWarnings(routeWarnings);
    }, [routeWarnings]);

    useEffect(() => {
        if (loadedBody && loadedIdRef.current !== loadedBody.id) {
            loadedIdRef.current = loadedBody.id;
            setForm(toFormState(loadedBody));
        }
    }, [loadedBody]);

    const selectedEndpoint = endpoints.find(
        (endpoint) =>
            endpoint.id.toLowerCase() === form.endpointId.toLowerCase(),
    );

    const endpointOptions = useMemo<EndpointOption[]>(
        () =>
            endpoints
                .filter(
                    (endpoint) =>
                        endpoint.isActive ||
                        endpoint.id.toLowerCase() ===
                            form.endpointId.toLowerCase(),
                )
                .map((endpoint) => ({
                    id: endpoint.id,
                    label: endpoint.isActive
                        ? endpoint.name
                        : `${endpoint.name} (disabled)`,
                })),
        [endpoints, form.endpointId],
    );
    const selectedOption = endpointOptions.find(
        (option) => option.id === selectedEndpoint?.id,
    );

    const update = <K extends keyof FormState>(key: K, value: FormState[K]) =>
        setForm((current) => ({ ...current, [key]: value }));

    const goToConfigurations = () => navigate("../..", { relative: "path" });

    const savedForm = useMemo(
        () => (body ? toFormState(body) : undefined),
        [body],
    );
    // A status change posts the saved values, so it would silently drop pending edits.
    const isDirty =
        !!savedForm &&
        (Object.keys(savedForm) as (keyof FormState)[]).some(
            (key) => form[key] !== savedForm[key],
        );
    const statusDisabledTooltip = isDirty
        ? "Save or discard your changes first."
        : undefined;

    const hasRequiredFields = !!form.name.trim() && !!form.endpointId;
    const jsonIsValid = jsonValidity.ruleMap && jsonValidity.requestBody;

    const toConfiguration = (): ConfigurationBody => {
        const parsed = parseJsonText(form.requestBody);
        const requestBody = isPlainObject(parsed) ? parsed : {};
        const otherEndpoints: EndPointReference[] = (
            body?.configuration?.endPoints ?? []
        ).slice(1);

        return {
            ...requestBody,
            RulesFilter: parseJsonText(form.ruleMap),
            endPoints: [{ integrationId: form.endpointId }, ...otherEndpoints],
        };
    };

    const save = async () => {
        setSaveErrors([]);
        setSaveWarnings([]);
        // Each field marks itself, and the banner repeats the reason: a required field left
        // empty was never touched, so its message can sit off screen while Save looks inert.
        const problems = [
            ruleMapRef.current?.validate(),
            requestBodyRef.current?.validate(),
        ].filter((problem): problem is string => !!problem);
        if (problems.length) {
            setSaveErrors(problems);
            return;
        }
        // An array, string or number is valid JSON but cannot carry the endpoint reference this
        // configuration is built from, and spreading one would discard what the user typed.
        if (
            form.requestBody.trim() &&
            !isPlainObject(parseJsonText(form.requestBody))
        ) {
            setSaveErrors([
                "Request Body must be a JSON object, not an array or a single value.",
            ]);
            return;
        }

        // A new emission body is created active, so it starts sending as soon as it exists.
        // Nothing is written until the dialog is confirmed.
        if (isNew) {
            setIsConfirmingCreate(true);
            return;
        }

        await persist();
    };

    const persist = async () => {
        setIsConfirmingCreate(false);
        setIsSaving(true);
        try {
            const name = form.name.trim();
            const description = form.description.trim() || null;
            const configuration = toConfiguration();
            if (isNew) {
                const created = await service.createEmissionBody(
                    name,
                    description,
                    configuration,
                );
                const createdWarnings = toWarningMessages(created.warnings);
                // The route changes to the new id while this component stays mounted, and the
                // list it would otherwise look the record up in has not been refetched yet.
                setSaved(created);
                setForm(toFormState(created));
                // The create response has already initialized this record. Do not let the
                // list reload replace the form with differently ordered JSON while the saved
                // baseline still comes from this response, making an untouched form dirty.
                loadedIdRef.current = created.id;
                setSaveWarnings(createdWarnings);
                messageStore.logMessage("Emission body created.", "success");
                reload();
                navigate(`../${created.id}`, {
                    relative: "path",
                    replace: true,
                    state: { emissionsWarnings: createdWarnings },
                });
            } else {
                const updated = await service.updateEmissionBody(
                    bodyId,
                    name,
                    description,
                    configuration,
                );
                setSaved(updated);
                setForm(toFormState(updated));
                setSaveWarnings(toWarningMessages(updated.warnings));
                messageStore.logMessage("Changes saved.", "success");
            }
        } catch (error) {
            setSaveErrors(await getSaveErrorMessages(error));
        } finally {
            setIsSaving(false);
        }
    };

    const changeStatus = async (isActive: boolean) => {
        // No configuration guard: a body with an unusable configuration is exactly the one that
        // has to stay switchable off.
        if (!body || !bodyId) return;
        setSaveErrors([]);
        setIsChangingStatus(true);
        try {
            const updated = await service.setEmissionBodyStatus(
                bodyId,
                isActive,
            );
            setSaved(updated);
            // Keep the displayed JSON and saved baseline in the same serialization order.
            setForm(toFormState(updated));
            messageStore.logMessage(
                isActive ? "Emission body enabled." : "Emission body disabled.",
                "success",
            );
        } catch (error) {
            setSaveErrors(await getSaveErrorMessages(error));
        } finally {
            setIsChangingStatus(false);
            setStatusDialog(undefined);
        }
    };

    if (isLoading) {
        return <AcxLoadingIndicator alternate="PuffLoader" size={64} />;
    }

    const title = isNew ? "New emission body" : body?.name ?? "Emission body";

    return (
        <Box
            sx={{
                padding: 4,
                maxWidth: `calc(100% - ${theme.spacing(4)})`,
                width: "1200px",
                display: "flex",
                flexDirection: "column",
                gap: 2,
            }}
        >
            <OrganizationBreadcrumbs
                additionalBreadcrumbs={[
                    { label: "Emissions", onClick: goToConfigurations },
                    ...(selectedEndpoint
                        ? [
                              {
                                  label: selectedEndpoint.name,
                                  onClick: () =>
                                      navigate(
                                          `../../endpoints/${selectedEndpoint.id}`,
                                          { relative: "path" },
                                      ),
                              },
                          ]
                        : []),
                    { label: title },
                ]}
            />

            {hasError && (
                <Alert
                    severity="error"
                    action={<AcxButton onClick={reload}>Retry</AcxButton>}
                >
                    Couldn’t load this emission body.
                </Alert>
            )}

            {!hasError && !isNew && !body && (
                <Alert severity="warning">
                    This emission body wasn’t found.{" "}
                    <Link component="button" onClick={goToConfigurations}>
                        Back to configurations
                    </Link>
                </Alert>
            )}

            {!hasError && (isNew || body) && (
                <>
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "12px",
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: "22px",
                                fontWeight: 700,
                                color: theme.palette.gray[700],
                            }}
                        >
                            {title}
                        </Typography>
                        <ConfigurationTypeChip type="body" />
                        {body && (
                            <ConfigurationStatusChip isActive={body.isActive} />
                        )}
                        {canEdit && (
                            <Box
                                sx={{
                                    marginLeft: "auto",
                                    display: "flex",
                                    gap: "10px",
                                }}
                            >
                                {body?.isActive && (
                                    <AcxButton
                                        variant="outlined"
                                        color="error"
                                        disabled={isSaving || isDirty}
                                        disabledTooltip={
                                            statusDisabledTooltip
                                        }
                                        onClick={() =>
                                            setStatusDialog("disable")
                                        }
                                    >
                                        Disable
                                    </AcxButton>
                                )}
                                {body && !body.isActive && (
                                    <AcxButton
                                        variant="outlined"
                                        color="primary"
                                        loading={isChangingStatus}
                                        disabled={isSaving || isDirty}
                                        disabledTooltip={
                                            statusDisabledTooltip
                                        }
                                        onClick={() =>
                                            setStatusDialog("enable")
                                        }
                                    >
                                        Enable
                                    </AcxButton>
                                )}
                                <AcxButton
                                    variant="contained"
                                    color="primary"
                                    loading={isSaving}
                                    disabled={
                                        !hasRequiredFields ||
                                        !jsonIsValid ||
                                        isChangingStatus
                                    }
                                    onClick={save}
                                >
                                    Save changes
                                </AcxButton>
                            </Box>
                        )}
                    </Box>

                    <EmissionsFormErrorBanner messages={saveErrors} />
                    <EmissionsFormErrorBanner
                        messages={saveWarnings}
                        variant="warning"
                    />

                    {isConfirmingCreate && (
                        <CreateBodyConfirmationDialog
                            name={form.name.trim()}
                            endpointName={selectedEndpoint?.name}
                            isLoading={isSaving}
                            onCancel={() => setIsConfirmingCreate(false)}
                            onConfirm={persist}
                        />
                    )}

                    {body && statusDialog && (
                        <ConfigurationStatusDialog
                            isOpen
                            action={statusDialog}
                            type="body"
                            name={body.name}
                            isLoading={isChangingStatus}
                            onCancel={() => setStatusDialog(undefined)}
                            onConfirm={() =>
                                changeStatus(statusDialog === "enable")
                            }
                        />
                    )}

                    <Box
                        sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1.25fr",
                            gap: "28px",
                        }}
                    >
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "14px",
                            }}
                        >
                            <AcxMainTextField
                                id="emissions-body-name"
                                labelText="Name"
                                required
                                value={form.name}
                                onChange={(evt) =>
                                    update("name", evt.target.value)
                                }
                                isDisabled={!canEdit}
                                fullWidth
                            />
                            <AcxMainTextField
                                id="emissions-body-description"
                                labelText="Description"
                                value={form.description}
                                onChange={(evt) =>
                                    update("description", evt.target.value)
                                }
                                isDisabled={!canEdit}
                                multiline
                                rowsMin={3}
                                rowsMax={8}
                                fullWidth
                            />
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "6px",
                                }}
                            >
                                <AcxSelectSingle
                                    id="emissions-body-endpoint"
                                    inputLabel="Endpoint Configuration"
                                    required
                                    options={endpointOptions}
                                    valueField="id"
                                    labelField="label"
                                    defaultValue={selectedOption}
                                    onChange={(option) =>
                                        update("endpointId", option.id)
                                    }
                                    isDisabled={!canEdit}
                                    isSearchable
                                    fullWidth
                                />
                                {selectedEndpoint && (
                                    <Typography
                                        sx={{
                                            fontSize: "12px",
                                            color: theme.palette.gray.main,
                                            overflowWrap: "anywhere",
                                        }}
                                    >
                                        Sends to {selectedEndpoint.endpointUri}
                                    </Typography>
                                )}
                            </Box>
                            <Box
                                sx={{
                                    marginTop: "6px",
                                    border: `1px solid ${theme.palette.gray[200]}`,
                                    borderRadius: "8px",
                                    padding: "12px 14px",
                                    fontSize: "12px",
                                    color: theme.palette.gray.main,
                                    lineHeight: 1.6,
                                }}
                            >
                                Authentication, retries and the destination URL
                                come from the endpoint. This body only defines{" "}
                                <Box
                                    component="strong"
                                    sx={{ color: theme.palette.gray[700] }}
                                >
                                    what
                                </Box>{" "}
                                is sent.
                            </Box>
                        </Box>

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "14px",
                            }}
                        >
                            <EmissionsJsonField
                                ref={ruleMapRef}
                                id="emissions-body-rule-map"
                                label="Rule Map"
                                headerLabel="Rule Map"
                                description="Which conversations trigger this emission. A conversation is sent only if it passes these rules."
                                required
                                value={form.ruleMap}
                                onChange={(value) => update("ruleMap", value)}
                                onValidityChange={(isValid) =>
                                    setJsonValidity((current) => ({
                                        ...current,
                                        ruleMap: isValid,
                                    }))
                                }
                                readonly={!canEdit}
                                rowsMax={10}
                            />
                            <EmissionsJsonField
                                ref={requestBodyRef}
                                id="emissions-body-request-body"
                                label="Configuration"
                                headerLabel="Request body"
                                required
                                value={form.requestBody}
                                onChange={(value) =>
                                    update("requestBody", value)
                                }
                                onValidityChange={(isValid) =>
                                    setJsonValidity((current) => ({
                                        ...current,
                                        requestBody: isValid,
                                    }))
                                }
                                readonly={!canEdit}
                                rowsMin={10}
                                rowsMax={24}
                            />
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
});

export default EmissionBodyForm;
